import { useState, useEffect, useCallback, useRef, useMemo } from 'react';

const useLoadRiteScale = (config) => {
    const [currentWeight, setCurrentWeight] = useState(null);
    const [weightStatus, setWeightStatus] = useState('stable');
    const [connected, setConnected] = useState(false);
    const [error, setError] = useState(null);
    const [lastUpdate, setLastUpdate] = useState(null);
    
    const pollingIntervalRef = useRef(null);
    const accessTokenRef = useRef(config?.loadriteAccessToken);
    const refreshTokenRef = useRef(config?.loadriteRefreshToken);

    // Check if this is a LoadRite configuration
    const isLoadRiteConfig = config && config.scaleType === 'loadrite';

    // Update token refs when config changes
    useEffect(() => {
        accessTokenRef.current = config?.loadriteAccessToken;
        refreshTokenRef.current = config?.loadriteRefreshToken;
    }, [config?.loadriteAccessToken || '', config?.loadriteRefreshToken || '']);

    const refreshAccessToken = useCallback(async () => {
        if (!refreshTokenRef.current) {
            throw new Error('No refresh token available');
        }

        try {
            const response = await fetch('https://apicloud.loadrite-myinsighthq.com/api/v2/auth/refresh-token', {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json'
                },
                body: JSON.stringify({
                    accessToken: accessTokenRef.current,
                    refreshToken: refreshTokenRef.current
                })
            });

            if (response.ok) {
                const data = await response.json();
                accessTokenRef.current = data.accessToken;
                refreshTokenRef.current = data.refreshToken;
                
                // Update config with new tokens
                if (window.electronAPI && window.electronAPI.secureStorage) {
                    const settings = await window.electronAPI.secureStorage.getSettings() || {};
                    settings.loadriteAccessToken = data.accessToken;
                    settings.loadriteRefreshToken = data.refreshToken;
                    await window.electronAPI.secureStorage.setSettings(settings);
                }
                
                return data.accessToken;
            } else {
                throw new Error(`Token refresh failed: ${response.status} ${response.statusText}`);
            }
        } catch (error) {
            console.error('LoadRite token refresh failed:', error);
            throw error;
        }
    }, []);

    const fetchLoadRiteData = useCallback(async () => {
        if (!isLoadRiteConfig || !config?.loadriteAccessToken || !config?.loadriteSiteId) {
            setError('LoadRite configuration incomplete');
            setConnected(false);
            return;
        }

        try {
            const endpoint = config?.loadriteDataType || 'NewWeight';
            let url = `https://apicloud.loadrite-myinsighthq.com/api/v2/${endpoint}?Site=${config.loadriteSiteId}`;
            
            // Add scale filter if specified
            if (config?.loadriteScaleId) {
                url += `&ScaleIds=${config.loadriteScaleId}`;
            }

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessTokenRef.current}`,
                    'Accept': 'application/json',
                    'Content-Type': 'application/json'
                }
            });

            if (response.status === 401) {
                // Token expired, try to refresh
                try {
                    const newToken = await refreshAccessToken();
                    // Retry with new token
                    const retryResponse = await fetch(url, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${newToken}`,
                            'Accept': 'application/json',
                            'Content-Type': 'application/json'
                        }
                    });
                    
                    if (retryResponse.ok) {
                        const data = await retryResponse.json();
                        processLoadRiteData(data);
                        setConnected(true);
                        setError(null);
                    } else {
                        throw new Error(`API Error after token refresh: ${retryResponse.status} ${retryResponse.statusText}`);
                    }
                } catch (refreshError) {
                    console.error('Token refresh failed:', refreshError);
                    setError('Authentication failed. Please check your tokens.');
                    setConnected(false);
                }
            } else if (response.ok) {
                const data = await response.json();
                processLoadRiteData(data);
                setConnected(true);
                setError(null);
            } else {
                throw new Error(`API Error: ${response.status} ${response.statusText}`);
            }
        } catch (error) {
            console.error('LoadRite API error:', error);
            setError(error.message);
            setConnected(false);
        }
    }, [config || {}, refreshAccessToken]);

    const processLoadRiteData = useCallback((data) => {
        if (!data || !Array.isArray(data) || data.length === 0) {
            setCurrentWeight(null);
            setWeightStatus('no_data');
            return;
        }

        // Get the most recent record
        const latestRecord = data[0];
        
        // Extract weight based on data type
        let weight = null;
        let status = 'stable';

        const dataType = config?.loadriteDataType || 'NewWeight';
        switch (dataType) {
            case 'NewWeight':
                weight = latestRecord.Weight || latestRecord.GrossWeight || latestRecord.NetWeight;
                status = latestRecord.Status || 'stable';
                break;
            case 'Loading':
                weight = latestRecord.GrossWeight || latestRecord.NetWeight;
                status = latestRecord.Status || 'stable';
                break;
            case 'Conveyor':
                weight = latestRecord.Weight || latestRecord.TotalWeight;
                status = latestRecord.Status || 'stable';
                break;
            case 'Haul':
                weight = latestRecord.GrossWeight || latestRecord.NetWeight;
                status = latestRecord.Status || 'stable';
                break;
            case 'Positions':
                weight = latestRecord.Weight;
                status = latestRecord.Status || 'stable';
                break;
            default:
                weight = latestRecord.Weight || latestRecord.GrossWeight || latestRecord.NetWeight;
                status = latestRecord.Status || 'stable';
        }

        if (weight !== null && weight !== undefined) {
            setCurrentWeight(weight);
            setWeightStatus(status);
            setLastUpdate(new Date());
        } else {
            setCurrentWeight(null);
            setWeightStatus('no_data');
        }
    }, [config?.loadriteDataType || 'NewWeight']);

    const startPolling = useCallback(() => {
        if (!isLoadRiteConfig) return;
        
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
        }

        const interval = config?.loadritePollingInterval || 5;
        pollingIntervalRef.current = setInterval(() => {
            fetchLoadRiteData();
        }, interval * 1000);

        // Initial fetch
        fetchLoadRiteData();
    }, [isLoadRiteConfig, config?.loadritePollingInterval || 5, fetchLoadRiteData]);

    const stopPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
        setConnected(false);
        setCurrentWeight(null);
        setError(null);
    }, []);

    // Start/stop polling based on config
    useEffect(() => {
        if (isLoadRiteConfig && config?.loadriteAccessToken && config?.loadriteSiteId) {
            startPolling();
        } else {
            stopPolling();
        }

        return () => {
            stopPolling();
        };
    }, [isLoadRiteConfig, config?.loadriteAccessToken || '', config?.loadriteSiteId || '', startPolling, stopPolling]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            stopPolling();
        };
    }, [stopPolling]);

    return useMemo(() => ({
        currentWeight: currentWeight || null,
        weightStatus: weightStatus || 'stable',
        connected: connected || false,
        error: error || null,
        lastUpdate: lastUpdate || null,
        startPolling: startPolling || (() => {}),
        stopPolling: stopPolling || (() => {}),
        fetchData: fetchLoadRiteData || (() => {})
    }), [currentWeight, weightStatus, connected, error, lastUpdate, startPolling, stopPolling, fetchLoadRiteData]);
};

export default useLoadRiteScale;
